import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { ptBR } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import { useSchedule } from "../../../../hooks/ScheduleContext";
// mesmo calendario dos filtros, de proposito: dois calendarios diferentes na
// mesma tela e a primeira coisa que faz a loja desconfiar de qual vale
import { Calendar } from "../../../../components/ui/Form/Input/styles";

import {
  CalendarPopup,
  Card,
  CurrentDate,
  DateAnchor,
  Day,
  StepButton,
  TodayButton,
  TopRow,
  Week,
} from "./styles";

const FORMATO = "YYYY-MM-DD";

// cinco anos para tras e dois para frente, igual ao calendario dos filtros
const ANO_ATUAL = new Date().getFullYear();
const PRIMEIRO_ANO = ANO_ATUAL - 5;
const ULTIMO_ANO = ANO_ATUAL + 2;

/**
 * Cabecalho de data da agenda.
 *
 * No lugar da regua de anos, meses e de uma tira longa de dias, mostra so a
 * semana do dia escolhido: e o que a loja usa no dia a dia. As setas andam um
 * dia por vez e passam de semana sozinhas; "Hoje" volta para o dia corrente.
 */
export function WeekStrip() {
  const { updateConfig, getConfig } = useSchedule();
  const config = getConfig();

  const [calendarioAberto, setCalendarioAberto] = useState(false);
  const ancoraRef = useRef(null);

  const selecionado = moment(config.date, FORMATO);
  const hoje = moment().format(FORMATO);

  /*
   * Fecha no clique fora e no Esc. Sem isso o calendario ficaria aberto por
   * cima da grade e a loja teria de clicar na data de novo para sumir com ele.
   */
  useEffect(() => {
    if (!calendarioAberto) return undefined;

    const cliqueFora = (evento) => {
      if (!ancoraRef.current?.contains(evento.target)) setCalendarioAberto(false);
    };
    const tecla = (evento) => {
      if (evento.key === "Escape") setCalendarioAberto(false);
    };

    document.addEventListener("mousedown", cliqueFora);
    document.addEventListener("keydown", tecla);

    return () => {
      document.removeEventListener("mousedown", cliqueFora);
      document.removeEventListener("keydown", tecla);
    };
  }, [calendarioAberto]);

  // duas semanas a partir do domingo da semana escolhida; quantos aparecem
  // depende da largura da tela (ver Week em styles.js)
  const dias = useMemo(() => {
    const inicio = selecionado.clone().startOf("week");
    return Array.from({ length: 14 }, (_, i) => inicio.clone().add(i, "day"));
    // eslint-disable-next-line
  }, [config.date]);

  const vaiPara = (data) => {
    const nova = data.format(FORMATO);
    if (nova === config.date) return;
    updateConfig({ date: nova });
  };

  return (
    <Card>
      <TopRow>
        <TodayButton type="button" onClick={() => vaiPara(moment())}>
          Hoje
        </TodayButton>

        <StepButton
          type="button"
          title="Dia anterior"
          aria-label="Dia anterior"
          onClick={() => vaiPara(selecionado.clone().subtract(1, "day"))}
        >
          <ChevronLeft size={20} />
        </StepButton>

        <StepButton
          type="button"
          title="Próximo dia"
          aria-label="Próximo dia"
          onClick={() => vaiPara(selecionado.clone().add(1, "day"))}
        >
          <ChevronRight size={20} />
        </StepButton>

        <DateAnchor ref={ancoraRef}>
          <CurrentDate
            type="button"
            aria-haspopup="dialog"
            aria-expanded={calendarioAberto}
            title="Escolher data"
            onClick={() => setCalendarioAberto((aberto) => !aberto)}
          >
            <CalendarDays size={18} aria-hidden="true" />
            {/*
              * Data curta, com ano de dois digitos: ela divide a linha com a
              * tira de dias agora, e por extenso ("quarta-feira, 4 de novembro
              * de 2026") nao sobrava largura para os dias. O dia da semana
              * continua ali, e a tira logo ao lado marca o dia escolhido.
              */}
            {selecionado.format("ddd, DD/MM/YY")}
          </CurrentDate>

          {calendarioAberto && (
            <CalendarPopup role="dialog" aria-label="Escolher data">
              <Calendar
                mode="single"
                selected={selecionado.toDate()}
                defaultMonth={selecionado.toDate()}
                /* mes e ano como seletores: a tira so mostra duas semanas, e
                   sem isso ir para outro mes custava dezenas de cliques na seta */
                captionLayout="dropdown-buttons"
                fromYear={PRIMEIRO_ANO}
                toYear={ULTIMO_ANO}
                onSelect={(data) => {
                  if (!data) return;
                  vaiPara(moment(data));
                  setCalendarioAberto(false);
                }}
                locale={ptBR}
              />
            </CalendarPopup>
          )}
        </DateAnchor>
      </TopRow>

      <Week role="group" aria-label="Dias">
        {dias.map((dia) => {
          const valor = dia.format(FORMATO);

          return (
            <Day
              key={valor}
              type="button"
              $selected={valor === config.date}
              $today={valor === hoje}
              aria-pressed={valor === config.date}
              onClick={() => vaiPara(dia)}
            >
              <strong>{dia.format("dddd").replace("-feira", "")}</strong>
              <span>{dia.format("D")}</span>
            </Day>
          );
        })}
      </Week>
    </Card>
  );
}
